import { useState } from 'react'
import { router, Link } from '@inertiajs/react'
import Sidebar from '../components/Sidebar'
import Logo from '../components/Logo'
import MemoryList from '../components/MemoryList'
import RightPanel from '../components/RightPanel'
import { useLocale } from '../lib/LocaleContext'

export default function Dashboard({ projects, sessions, memories, filters, active_session, workspaces, stats }) {
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [selectedMemory, setSelectedMemory] = useState(null)
  const { t } = useLocale()

  function handleSignOut() {
    const form = document.createElement("form")
    form.method = "POST"
    form.action = "/logout"
    const method = document.createElement("input")
    method.type = "hidden"
    method.name = "_method"
    method.value = "DELETE"
    const csrf = document.querySelector('meta[name="csrf-token"]')
    const token = document.createElement("input")
    token.type = "hidden"
    token.name = "authenticity_token"
    token.value = csrf ? csrf.content : ""
    form.appendChild(method)
    form.appendChild(token)
    document.body.appendChild(form)
    form.submit()
  }

  return (
    <div className="min-h-screen bg-zinc-900 text-zinc-100 flex flex-col">
      {/* Header */}
      <header className="border-b border-zinc-700 px-6 py-3 flex items-center justify-between shrink-0 bg-gradient-to-r from-zinc-900 via-zinc-900 to-zinc-800/30">
        <Link href="/" className="flex items-center gap-2.5 no-underline group">
          <Logo variant="mark" size={28} />
          <span className="text-base font-mono font-bold tracking-[0.12em] uppercase text-zinc-100 group-hover:text-emerald-300 transition-colors">
            anamnesis
          </span>
        </Link>
        <button
          onClick={handleSignOut}
          className="text-[11px] font-mono text-zinc-300 hover:text-white border border-zinc-600 hover:border-zinc-400 rounded-md px-3 py-1 bg-zinc-800/50 hover:bg-zinc-800 transition-colors"
        >
          {t('dashboard.sign_out')}
        </button>
      </header>

      {/* Stats bar */}
      <div className="border-b border-zinc-700 px-6 py-3 shrink-0">
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-gradient-to-br from-emerald-950/40 to-zinc-900 border border-zinc-700 rounded-lg px-4 py-3">
            <p className="text-xs font-mono text-zinc-500 uppercase tracking-wider">{t('dashboard.stats.active')}</p>
            <p className="text-2xl font-mono font-bold text-emerald-400 mt-1">{stats?.total_active ?? 0}</p>
          </div>
          <div className="bg-gradient-to-br from-zinc-800/60 to-zinc-900 border border-zinc-700 rounded-lg px-4 py-3">
            <p className="text-xs font-mono text-zinc-500 uppercase tracking-wider">{t('dashboard.stats.archived')}</p>
            <p className="text-2xl font-mono font-bold text-zinc-400 mt-1">{stats?.total_archived ?? 0}</p>
          </div>
          <div className="bg-gradient-to-br from-sky-950/40 to-zinc-900 border border-zinc-700 rounded-lg px-4 py-3">
            <p className="text-xs font-mono text-zinc-500 uppercase tracking-wider">{t('dashboard.stats.sessions')}</p>
            <p className="text-2xl font-mono font-bold text-sky-400 mt-1">{stats?.open_sessions ?? 0}</p>
          </div>
          <div className="bg-gradient-to-br from-amber-950/40 to-zinc-900 border border-zinc-700 rounded-lg px-4 py-3">
            <p className="text-xs font-mono text-zinc-500 uppercase tracking-wider">{t('dashboard.stats.llm_cost')}</p>
            <p className="text-2xl font-mono font-bold text-amber-400 mt-1">${Number(stats?.llm_cost?.total_usd ?? 0).toFixed(4)}</p>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1">
        <div className="relative flex-shrink-0">
          <Sidebar
            projects={projects ?? []}
            sessions={sessions ?? []}
            workspaces={workspaces ?? []}
            active_project={filters?.project ?? null}
            active_session={active_session ?? null}
            onNavigate={() => setSidebarOpen(false)}
            open={sidebarOpen}
          />
          <button
            onClick={() => setSidebarOpen(o => !o)}
            className="absolute top-4 -right-3 z-10 w-6 h-6 flex items-center justify-center bg-zinc-800 border border-zinc-700 rounded-full text-zinc-400 hover:text-white text-xs"
            aria-label="Toggle sidebar"
          >
            {sidebarOpen ? '‹' : '›'}
          </button>
        </div>

        <main className="flex-1 p-6 min-w-0">
          <MemoryList
            memories={memories ?? { data: [], total_count: 0, total_pages: 1, current_page: 1, per_page: 5 }}
            filters={filters ?? {}}
            active_session={active_session ?? null}
            projects={projects ?? []}
            onSelect={setSelectedMemory}
          />
        </main>

        {selectedMemory && (
          <RightPanel
            memory={selectedMemory}
            activeSession={active_session ?? null}
            onClose={() => setSelectedMemory(null)}
          />
        )}
      </div>
    </div>
  )
}
